// IssueEvidencePanel — "Issue evidence" tab on the Performance grade
// drawer. Surfaces the four issue signals the review meeting should
// consider, AS EVIDENCE: severity counts, median time to resolve,
// time-in-awaiting, overdue-action ratio, plus a standalone-issues
// callout for issues with no link to an already-scored register item.
// Crucially the panel makes it explicit that issues do NOT alter the
// A–E letter grade — they inform the qualitative review only.

import { useMemo } from "react";
import { Link } from "react-router";
import { Flag, Info, Loader2 } from "lucide-react";
import { useAllIssuesForBackLinkCount } from "../../../hooks/useTracking";
import { getBrisbaneToday } from "../../../utils/getBrisbaneToday";
import {
  ISSUE_SEVERITY_DOT_CLASS,
  ISSUE_SEVERITY_LABEL,
} from "../tracking/issue/issueSeverityPalette";
import { computeIssueEvidence } from "./issueEvidenceMetrics";

export interface IssueEvidencePanelProps {
  contractId: string;
  /** Pinned `today` (UTC day ISO) — tests inject a stable value; production
   *  falls back to Brisbane today. */
  today?: string;
}

function formatRatio(r: number | null): string {
  if (r === null) return "—";
  return `${Math.round(r * 100)}%`;
}

function formatDays(d: number | null): string {
  if (d === null) return "—";
  if (d === 1) return "1 day";
  return `${d} days`;
}

export default function IssueEvidencePanel({
  contractId,
  today,
}: IssueEvidencePanelProps) {
  const { data: issues, isPending } = useAllIssuesForBackLinkCount(contractId);
  const todayIso = today ?? getBrisbaneToday();
  const evidence = useMemo(
    () => computeIssueEvidence(issues, todayIso),
    [issues, todayIso],
  );

  if (isPending) {
    return (
      <div
        className="flex items-center gap-2 p-6 text-body-sm text-slate-500 dark:text-slate-400"
        data-testid="issue-evidence-loading"
      >
        <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
        Loading issue evidence…
      </div>
    );
  }

  return (
    <div className="space-y-5 p-6" data-testid="issue-evidence-panel">
      {/* Informational note — kept neutral slate so the brand-toned banner
         family (Phase 2's linked-filter banner) remains the operational
         "you have a filter on" signal. Only the Info icon carries brand
         violet to anchor the panel as an evidence surface. */}
      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-caption text-slate-700 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-300">
        <Info
          aria-hidden="true"
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600 dark:text-brand-400"
        />
        <span>
          Issues are <strong>evidence</strong> — they inform the qualitative
          review but do not alter the A–E grade. The four signals below
          surface the volume, recoverability and supplier-response posture
          on the register.
        </span>
      </div>

      <section aria-labelledby="issue-evidence-severity-h">
        <h3
          id="issue-evidence-severity-h"
          className="mb-2 text-overline font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Issues by severity
        </h3>
        <ul
          data-testid="issue-evidence-severity"
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {evidence.severityCounts.map((c) => (
            <li
              key={c.severity}
              className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/40"
            >
              <div className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={`inline-block h-2 w-2 rounded-full ${ISSUE_SEVERITY_DOT_CLASS[c.severity]}`}
                />
                <span className="text-caption font-medium text-slate-600 dark:text-slate-300">
                  {ISSUE_SEVERITY_LABEL[c.severity]}
                </span>
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-h4 font-semibold text-slate-900 dark:text-slate-100">
                  {c.open}
                </span>
                <span className="text-caption text-slate-500 dark:text-slate-400">
                  open of {c.total}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section
        aria-labelledby="issue-evidence-metrics-h"
        className="grid grid-cols-1 gap-3 sm:grid-cols-3"
      >
        <h3 id="issue-evidence-metrics-h" className="sr-only">
          Lifecycle metrics
        </h3>
        <MetricTile
          testId="issue-evidence-time-to-resolve"
          label="Median time to resolve"
          value={formatDays(evidence.medianTimeToResolveDays)}
          caption="Median for issues actually resolved (not withdrawn or closed without resolution)"
        />
        <MetricTile
          testId="issue-evidence-time-awaiting"
          label="Median time awaiting"
          value={formatDays(evidence.medianTimeInAwaitingDays)}
          caption="Currently in Awaiting response"
        />
        <MetricTile
          testId="issue-evidence-overdue-ratio"
          label="Overdue actions"
          value={formatRatio(evidence.overdueActionRatio)}
          caption={
            evidence.totalActions === 0
              ? "No actions raised"
              : `${evidence.overdueActions} of ${evidence.totalActions} actions`
          }
        />
      </section>

      <section aria-labelledby="issue-evidence-standalone-h">
        <h3
          id="issue-evidence-standalone-h"
          className="mb-2 text-overline font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          Standalone open issues
        </h3>
        {evidence.standaloneOpenIssues.length === 0 ? (
          <p
            className="text-body-sm text-slate-500 dark:text-slate-400"
            data-testid="issue-evidence-standalone-empty"
          >
            No standalone issues — every open issue is linked to a tracked
            register item.
          </p>
        ) : (
          <ul
            data-testid="issue-evidence-standalone-list"
            className="space-y-1"
          >
            {evidence.standaloneOpenIssues.map((i) => (
              <li key={i.id}>
                {/* Each row deep-links to the issue's drawer via ?item=, so
                    the card chrome reads as an actionable affordance and
                    the reviewer can navigate from evidence → detail in one
                    click. Hover + focus rings make the interactivity
                    obvious; the brand focus ring matches Phase 2's chip
                    + the drawer's other affordances. */}
                <Link
                  to={`?tab=issue&item=${encodeURIComponent(i.id)}`}
                  className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2 text-body-sm hover:border-slate-300 hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-700 dark:bg-slate-800/40 dark:hover:border-slate-600 dark:hover:bg-slate-800/60"
                >
                  <Flag
                    aria-hidden="true"
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-800 dark:text-slate-100">
                      {i.title}
                    </div>
                    <div className="mt-0.5 inline-flex items-center gap-1.5 text-caption text-slate-500 dark:text-slate-400">
                      <span className="font-mono">{i.issueRef}</span>
                      <span aria-hidden="true">·</span>
                      <span
                        aria-hidden="true"
                        className={`inline-block h-2 w-2 rounded-full ${ISSUE_SEVERITY_DOT_CLASS[i.severity]}`}
                      />
                      <span>{ISSUE_SEVERITY_LABEL[i.severity]}</span>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function MetricTile({
  testId,
  label,
  value,
  caption,
}: {
  testId: string;
  label: string;
  value: string;
  caption: string;
}) {
  return (
    <div
      data-testid={testId}
      className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800/40"
    >
      <div className="text-caption text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-h4 font-semibold text-slate-900 dark:text-slate-100">
        {value}
      </div>
      <div className="mt-1 text-caption text-slate-500 dark:text-slate-400">
        {caption}
      </div>
    </div>
  );
}
